import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type PlaybackStatus = "idle" | "loading" | "playing" | "failed";

/** The slice of HTMLAudioElement the provider uses; tests inject a fake. */
export interface AudioLike {
  src: string;
  currentTime: number;
  readonly duration: number;
  play(): Promise<void>;
  pause(): void;
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

export interface Playback {
  /** Id of the clip that owns the element, or null. */
  activeId: string | null;
  status: PlaybackStatus;
  positionMs: number;
  durationMs: number;
  /** Starts `src` under `id`; anything else playing stops, because there is one element. */
  play: (id: string, src: string) => void;
  pause: () => void;
  /** Seek the active clip to a fraction 0..1. */
  seek: (fraction: number) => void;
}

const PlaybackContext = createContext<Playback | null>(null);

/**
 * One audio element for the whole SPA, so a Sample, a Pronunciation Check, and
 * a Chapter listen can never play over each other. `createAudio` is injectable
 * for tests; the default is a real `Audio`.
 *
 * The element is built once, in a lazy `useState` initializer, and every effect
 * and callback below depends on that element rather than on the factory. A
 * default parameter is a fresh arrow on every render, so keying the listener
 * effect on the factory would tear the listeners down and call `pause()` on
 * every state change: the `setStatus("loading")` in `play` would abort the
 * `play()` promise it had just started and the clip would read as failed.
 * `createAudio` is therefore read at mount and ignored afterwards.
 */
export function AudioProvider({
  children,
  createAudio = () => new Audio(),
}: {
  children: ReactNode;
  createAudio?: () => AudioLike;
}) {
  const [el] = useState<AudioLike>(createAudio);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [status, setStatus] = useState<PlaybackStatus>("idle");
  const [positionMs, setPositionMs] = useState(0);
  const [durationMs, setDurationMs] = useState(0);

  useEffect(() => {
    const onTime = () => setPositionMs(el.currentTime * 1000);
    const onDuration = () => setDurationMs(Number.isFinite(el.duration) ? el.duration * 1000 : 0);
    const onPlaying = () => setStatus("playing");
    const onPause = () => setStatus((s) => (s === "playing" ? "idle" : s));
    const onEnded = () => {
      setStatus("idle");
      setPositionMs(0);
    };
    const onError = () => setStatus("failed");
    const onWaiting = () => setStatus((s) => (s === "playing" ? "loading" : s));
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("durationchange", onDuration);
    el.addEventListener("loadedmetadata", onDuration);
    el.addEventListener("playing", onPlaying);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnded);
    el.addEventListener("error", onError);
    el.addEventListener("waiting", onWaiting);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("durationchange", onDuration);
      el.removeEventListener("loadedmetadata", onDuration);
      el.removeEventListener("playing", onPlaying);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnded);
      el.removeEventListener("error", onError);
      el.removeEventListener("waiting", onWaiting);
      el.pause();
    };
  }, [el]);

  const play = useCallback(
    (id: string, src: string) => {
      if (activeId !== id || el.src !== src) {
        el.pause();
        el.src = src;
        el.currentTime = 0;
        setPositionMs(0);
        setDurationMs(0);
        setActiveId(id);
      }
      setStatus("loading");
      el.play().catch(() => setStatus("failed"));
    },
    [el, activeId],
  );

  const pause = useCallback(() => {
    el.pause();
    setStatus("idle");
  }, [el]);

  const seek = useCallback(
    (fraction: number) => {
      if (!Number.isFinite(el.duration) || el.duration <= 0) return;
      const next = Math.min(1, Math.max(0, fraction)) * el.duration;
      el.currentTime = next;
      setPositionMs(next * 1000);
    },
    [el],
  );

  const value = useMemo<Playback>(
    () => ({ activeId, status, positionMs, durationMs, play, pause, seek }),
    [activeId, status, positionMs, durationMs, play, pause, seek],
  );
  return <PlaybackContext.Provider value={value}>{children}</PlaybackContext.Provider>;
}

export function usePlayback(): Playback {
  const ctx = useContext(PlaybackContext);
  if (!ctx) throw new Error("usePlayback needs an AudioProvider above it");
  return ctx;
}

/** The playback of one clip: its own status, and toggle/seek bound to its id and src. */
export function useClip(id: string, src: string | undefined) {
  const playback = usePlayback();
  const active = playback.activeId === id;
  const status: PlaybackStatus = active ? playback.status : "idle";
  const toggle = useCallback(() => {
    if (!src) return;
    if (active && playback.status === "playing") playback.pause();
    else playback.play(id, src);
  }, [active, id, src, playback]);
  return {
    active,
    status,
    positionMs: active ? playback.positionMs : 0,
    durationMs: active ? playback.durationMs : 0,
    toggle,
    seek: active ? playback.seek : undefined,
  };
}
